/**
 * Spec 212 T033 (owner decision 8) — how a recovery point was copied, in words that claim no more than the run recorded.
 *
 * The mover and the copy check come from the run row (recoveryPointRepository.listForTarget). A lakehouse's check is
 * that every copied file's destination size equalled its source size: no content is hashed. A warehouse records no
 * check. A run from before these columns existed records neither, and says "Not recorded" rather than a guess.
 */
import type { BackupEntityType } from '../../api/restore';

export interface ProvenanceText {
  label: string;
  tip: string;
}

export interface CopyCheckText extends ProvenanceText {
  /** Badge variant when there is a status to show; null renders plain text. */
  tone: 'success' | 'danger' | null;
}

export function moverLabel(entityType: BackupEntityType, mover: string | null | undefined): ProvenanceText {
  if (mover === 'server_side') {
    return {
      label: 'Server-side copy',
      tip: 'Storage copied each file directly from OneLake; nothing passed through the backup container.',
    };
  }
  if (mover === 'streaming') {
    return entityType === 'warehouse'
      ? { label: 'T-SQL export', tip: "The warehouse's tables were exported through the backup container." }
      : { label: 'Streaming copy (retired)', tip: 'Copied through the backup container, a method no longer used for lakehouses.' };
  }
  return { label: 'Not recorded', tip: 'This backup ran before the platform recorded how it was copied.' };
}

/** Spec 212 T014: how many of the point's files Azure Storage checked against their source MD5 (migration 409). */
export interface HashCounts {
  hashChecked: number | null | undefined;
  files: number | null | undefined;
}

export function copyCheck(
  entityType: BackupEntityType,
  verified: boolean | null | undefined,
  counts?: HashCounts,
): CopyCheckText {
  if (verified === true) {
    const hashed = counts?.hashChecked;
    const files = counts?.files;
    if (hashed != null && hashed > 0 && files != null && files >= hashed) {
      const rest = files - hashed;
      return {
        label: 'Hashes match',
        tone: 'success',
        tip:
          rest === 0
            ? `All ${files} files were checked against their source MD5 by Azure Storage.`
            : `${hashed} of ${files} files were checked against their source MD5 by Azure Storage; the other ${rest} (over 256 MiB, or with no source MD5) matched in size.`,
      };
    }
    return {
      label: 'Sizes match',
      tone: 'success',
      tip: "Every copied file's size matched its source. File contents are not compared.",
    };
  }
  if (verified === false) {
    return { label: 'Check failed', tone: 'danger', tip: "At least one copied file's size did not match its source." };
  }
  return entityType === 'warehouse'
    ? { label: 'Not recorded', tone: null, tip: "Warehouse backups don't record a copy check." }
    : { label: 'Not recorded', tone: null, tip: 'This backup ran before the copy check was recorded.' };
}
